import { Component, EventEmitter, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { renderFields } from "@highnoteplatform/card-viewer";
import { LogHelper } from '../../../../../../core/helpers/log.helper';
import { AlertService } from '../../../../../../shared/alert/alert.service';
import { environment } from '../../../../../../../environments/environment';

@Component({
  selector: 'app-highnote-card-viewer',
  templateUrl: './highnote-card-viewer.component.html',
  styleUrls: ['./highnote-card-viewer.component.scss']
})
export class HighnoteCardViewerComponent implements OnInit, OnDestroy {
  @Output() retryRequired = new EventEmitter<void>();

  clientToken = "";
  paymentCardId = "";
  private toggleMask?: () => void;
  private renderedFields: Awaited<ReturnType<typeof renderFields>> | null = null;
  private loadingTimeout: any;
  private destroyed = false;
  private ready = false;
  private retryCount = 0;
  private retrying = false;
  private renderGeneration = 0;
  private renderChain: Promise<void> = Promise.resolve();
  private readyGate: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;
  private handshakeRetryCount = 0;
  private handshakeEscalated = false;
  private readonly maxRetries = 3;
  private readonly maxHandshakeRetries = 2;
  private readonly readyGraceMs = 4000;
  private readonly fieldSelectors = ['#cardNumber', '#cvv', '#expirationDate'];
  loading = true;

  constructor(
    private alertService: AlertService,
    private zone: NgZone) { }

  async visualiseCard(clientToken: string, paymentCardId: string) {
    if (!this.retrying)
    {
      this.retryCount = 0;
      this.handshakeRetryCount = 0;
      this.handshakeEscalated = false;
    }
    this.retrying = false;

    this.clientToken = clientToken;
    this.paymentCardId = paymentCardId;
    this.toggleMask = undefined;
    this.loading = true;
    await this.queueRender();
  }

  async ngOnInit() {
    if (this.clientToken && this.paymentCardId)
    {
      await this.queueRender();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    clearTimeout(this.loadingTimeout);
    this.renderedFields?.unmount().catch((error) => LogHelper.error("Highnote Unmount Error:", error));
  }

  onToggleMask() {
    if (this.toggleMask)
    {
      this.toggleMask();
    }
  }

  // Renders are queued rather than run concurrently: switching cards quickly would otherwise leave
  // two renderFields calls interleaved, stacking iframes and losing whichever ready event arrives.
  private queueRender(): Promise<void> {
    const generation = ++this.renderGeneration;
    this.renderChain = this.renderChain.then(() => this.initializeCard(generation));

    return this.renderChain;
  }

  private isStale(generation: number): boolean {
    return this.destroyed || generation !== this.renderGeneration;
  }

  private clearContainers() {
    this.fieldSelectors.forEach(selector => {
      const el = document.querySelector(selector);
      if (el)
      {
        el.innerHTML = '';
      }
    });
  }

  private async cleanupPreviousRender() {
    if (this.renderedFields)
    {
      await this.renderedFields.unmount().catch((error) => LogHelper.error("Highnote Unmount Error:", error));
      this.renderedFields = null;
    }
    else
    {
      this.clearContainers();
    }
  }

  // renderFields falls back to document.body when a selector misses, so the mount points have
  // to exist before it runs or the card renders offscreen with no error reported.
  private async mountTargetsReady(generation: number): Promise<boolean> {
    for (let attempt = 0; attempt < 20; attempt++)
    {
      if (this.isStale(generation))
      {
        return false;
      }

      if (this.fieldSelectors.every(selector => document.querySelector(selector) != null))
      {
        return true;
      }

      await new Promise<void>(resolve => setTimeout(resolve));
    }

    return false;
  }

  private awaitFramesLoaded(): Promise<void> {
    const frames = this.fieldSelectors
      .map(selector => document.querySelector(selector)?.querySelector('iframe'))
      .filter((frame): frame is HTMLIFrameElement => frame != null);

    if (!frames.length)
    {
      return Promise.resolve();
    }

    const loaded = Promise.all(frames.map(frame => new Promise<void>(resolve => {
      frame.addEventListener('load', () => resolve(), { once: true });
      frame.addEventListener('error', () => resolve(), { once: true });
    })));

    return Promise.race([loaded.then(() => undefined), new Promise<void>(resolve => setTimeout(resolve, 8000))]);
  }

  // Chrome keeps presenting the surface each field frame had before it painted, so the values stay
  // invisible until a mousemove invalidates the tile. Resizing a frame forces the child to submit a
  // fresh surface, which is the only nudge that reliably crosses the process boundary — display:none
  // would work too but tearing a frame down loses the SDK's one-shot data broadcast.
  private repaintFrames(generation: number) {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        if (this.isStale(generation))
        {
          return;
        }

        const frames = this.fieldSelectors
          .map(selector => document.querySelector(selector)?.querySelector('iframe'))
          .filter((frame): frame is HTMLIFrameElement => frame != null);

        frames.forEach(frame => {
          frame.style.height = 'calc(100% + 1px)';
        });

        requestAnimationFrame(() => {
          frames.forEach(frame => {
            frame.style.height = '';
          });
        });
      });
    });
  }

  private openReadyGate() {
    this.readyGate = new Promise<void>(resolve => {
      this.readyResolve = resolve;
    });
  }

  // onReady is the only trustworthy success signal: the SDK emits CARD_VIEWER_READY only once every
  // field frame has received the card data and painted, so its absence means a field is still blank.
  private async awaitRenderReady(generation: number): Promise<boolean> {
    const gate = this.readyGate ?? Promise.resolve();
    await this.awaitFramesLoaded();

    if (this.isStale(generation))
    {
      return false;
    }

    await Promise.race([gate, new Promise<void>(resolve => setTimeout(resolve, this.readyGraceMs))]);

    return this.ready;
  }

  // The SDK broadcasts the card data to its field frames exactly once and cannot replay it, so a frame
  // that loaded too late stays blank forever. Only a fresh render recovers it, and that render reliably
  // wins the race with the CDN bundles now cached.
  private onHandshakeFailure(generation: number) {
    this.zone.run(() => {
      if (this.isStale(generation))
      {
        return;
      }

      clearTimeout(this.loadingTimeout);
      LogHelper.error("Highnote Error:", "Card viewer fields did not report ready.");

      if (this.handshakeRetryCount < this.maxHandshakeRetries)
      {
        this.handshakeRetryCount++;
        this.queueRender().catch((error) => LogHelper.error("Highnote Re-render Error:", error));
        return;
      }

      if (!this.handshakeEscalated)
      {
        this.handshakeEscalated = true;
        this.retrying = true;
        this.retryRequired.emit();
        return;
      }

      this.alertService.showWarningAlert("Failed to render card details.");
      this.loading = false;
    });
  }

  private onRenderError(error: unknown, generation: number) {
    const errorName = (error as { name?: string }).name;

    // The SDK forwards every postMessage it has no case for — including card-fetch success and
    // mask toggles — to onError without a name, so retrying on those restarts a working render.
    if (!errorName)
    {
      LogHelper.info("Highnote card viewer ignored an unhandled message:", error);
      return;
    }

    if (errorName === "CopyToClipboardError")
    {
      return;
    }

    this.zone.run(() => {
      if (this.isStale(generation) || this.ready)
      {
        return;
      }

      clearTimeout(this.loadingTimeout);
      LogHelper.error("Highnote Error:", error);

      if (this.retryCount < this.maxRetries)
      {
        this.retryCount++;
        this.retrying = true;
        this.retryRequired.emit();
      }
      else
      {
        this.alertService.showWarningAlert("Failed to render card details.");
        this.loading = false;
      }
    });
  }

  private async initializeCard(generation: number) {
    if (this.isStale(generation))
    {
      return;
    }

    this.ready = false;
    this.loading = true;
    this.openReadyGate();
    await this.cleanupPreviousRender();

    if (!await this.mountTargetsReady(generation))
    {
      if (!this.destroyed && generation === this.renderGeneration)
      {
        this.zone.run(() => {
          LogHelper.error("Highnote Error:", "Card viewer mount points were unavailable.");
          this.alertService.showWarningAlert("Technical error while loading card viewer.");
          this.loading = false;
        });
      }

      return;
    }

    clearTimeout(this.loadingTimeout);

    // renderFields resolves as soon as the iframes are appended, so onReady is the only signal the card
    // actually rendered — this is the last resort that clears the spinner.
    this.loadingTimeout = setTimeout(() => {
      this.zone.run(() => {
        if (!this.isStale(generation) && this.loading)
        {
          if (!this.ready)
          {
            this.alertService.showWarningAlert("Failed to render card details.");
          }

          this.loading = false;
        }
      });
    }, 15000);

    try
    {
      const rendered = await renderFields({
        clientToken: this.clientToken,
        paymentCardId: this.paymentCardId,
        enableClipboard: false,
        environment: (environment.environment === "DEVELOPMENT" || environment.environment === "STAGING" || environment.environment === "VALIDATION") ? "test" : "live",
        onError: (error) => {
          this.onRenderError(error, generation);
        },
        onReady: () => {
          this.zone.run(() => {
            if (this.isStale(generation))
            {
              return;
            }

            clearTimeout(this.loadingTimeout);
            this.ready = true;
            this.retryCount = 0;
            this.retrying = false;
            this.loading = false;
            this.readyResolve?.();
            this.repaintFrames(generation);
          });
        },
        elements: {
          cardNumber: {
            selector: "#cardNumber",
            styles: {
              color: "white",
              fontSize: "21px",
              letterSpacing: "0.25em",
              fontFamily: '"OCR A Std", "monospace"',
              fontWeight: "500",
              lineHeight: "40px",
            }
          },
          cvv: {
            selector: "#cvv",
            styles: {
              color: "white",
              fontSize: "17px",
              fontFamily: "monospace",
              fontWeight: "600",
              lineHeight: "30px",
            }
          },
          expirationDate: {
            selector: "#expirationDate",
            styles: {
              color: "white",
              fontSize: "17px",
              fontFamily: "monospace",
              fontWeight: "600",
              lineHeight: "30px",
            }
          },
        },
      });

      if (this.isStale(generation))
      {
        await rendered.unmount().catch((error) => LogHelper.error("Highnote Unmount Error:", error));
        return;
      }

      this.renderedFields = rendered;
      this.toggleMask = rendered.toggleCardNumberMask;

      if (!await this.awaitRenderReady(generation))
      {
        this.onHandshakeFailure(generation);
      }
    }
    catch (error)
    {
      clearTimeout(this.loadingTimeout);

      if (this.isStale(generation))
      {
        return;
      }

      this.zone.run(() => {
        LogHelper.error("Highnote Initialization Error:", error);
        this.alertService.showWarningAlert("Technical error while loading card viewer.");
        this.loading = false;
      });
    }
  }
}
